import { colors } from '../../../lib/colors';
import { escapeRegex, readPattern } from '../GscTools/regex';

export { escapeRegex };

/**
 * The brand rule this report is built on: the user's own definition of which queries are theirs.
 *
 * Nothing here is Google's. Search Console has no brand classifier and returns no such field, so
 * every figure on this report is the consequence of a rule the user wrote — which is why the rule
 * is compiled in the open, shown as the expression that was actually sent, and named in the
 * export beside the numbers it produced.
 *
 * The rule becomes exactly one RE2 expression, used twice: once with `includingRegex` for the
 * brand side and once with `excludingRegex` for the non-brand side. One expression, two
 * complementary filters, so the two sides cannot overlap and cannot disagree about where a query
 * belongs. The complement is asked of Search Console rather than computed here — subtracting
 * brand from the property total would silently absorb every query Google withheld into
 * "non-brand".
 */

/**
 * How the terms become a pattern.
 *
 * Literal is the default and is what most people mean by "my brand": the text they typed, matched
 * case-insensitively wherever it appears in a query, with every metacharacter escaped so a dot in
 * a product name cannot quietly match any character. Advanced hands the expression to Search
 * Console verbatim and is explicit about it — RE2 is not JavaScript's regular expression language,
 * so a pattern this app accepts can still come back from the API as an error.
 */
export const MATCH_POLICY = { LITERAL: 'literal', REGEX: 'regex' };

export const MATCH_POLICIES = [MATCH_POLICY.LITERAL, MATCH_POLICY.REGEX];

/** The two sides of the rule, and the Search Console operator each one is. */
export const SEGMENT = { BRAND: 'brand', NON_BRAND: 'non_brand' };

export const SEGMENTS = [SEGMENT.BRAND, SEGMENT.NON_BRAND];

export const SEGMENT_OPERATOR = {
    [SEGMENT.BRAND]: 'includingRegex',
    [SEGMENT.NON_BRAND]: 'excludingRegex',
};

/**
 * One colour per side, taken from the shared chart palette so the card, the line and the legend
 * entry for a segment are the same colour everywhere on the page.
 */
export const SEGMENT_COLORS = {
    [SEGMENT.BRAND]: colors[0],
    [SEGMENT.NON_BRAND]: colors[1],
};

export function segmentLabel(t, segment) {
    return t(`tools.brand_non_brand.segments.${segment}`);
}

/** Product bounds on one definition. They bound the compiled pattern without capping it twice. */
export const MAX_TERM_LENGTH = 200;
export const MAX_TERMS = 25;
export const MAX_PATTERN_LENGTH = 2000;

/** An empty row of the terms editor. `name` is a label for the user and is never sent. */
export function emptyTerm() {
    return { name: '', match: '' };
}

/**
 * Whether a pattern is syntactically usable, as a reason or null.
 *
 * This is a gate, not a validator: RE2 and JavaScript's RegExp are close but not the same
 * language, so a pattern that passes here can still be rejected by Search Console, and that
 * rejection surfaces as the request's own error rather than being guessed at in the browser.
 */
export function patternError(pattern, t) {
    const value = (pattern ?? '').trim();
    if (!value) return t('tools.brand_non_brand.errors.no_pattern');

    if (value.length > MAX_PATTERN_LENGTH) {
        return t('tools.brand_non_brand.errors.pattern_long', { max: MAX_PATTERN_LENGTH });
    }

    try {
        readPattern(value);
    } catch (err) {
        return t('tools.brand_non_brand.errors.invalid_regex', { reason: err.message });
    }

    return null;
}

/** Why one typed term cannot be added, or null when it can. */
export function termError(term, terms, t) {
    const match = (term?.match ?? '').trim();

    if (!match) return t('tools.brand_non_brand.errors.empty_term');
    if (match.length > MAX_TERM_LENGTH) {
        return t('tools.brand_non_brand.errors.term_long', { max: MAX_TERM_LENGTH });
    }
    if ((terms ?? []).length >= MAX_TERMS) {
        return t('tools.brand_non_brand.errors.too_many_terms', { max: MAX_TERMS });
    }

    // Case is not part of a literal term — the compiled pattern is case-insensitive — so two
    // terms differing only in case would be the same alternative written twice.
    const exists = (terms ?? []).some(
        (item) => item.match.trim().toLowerCase() === match.toLowerCase(),
    );
    if (exists) return t('tools.brand_non_brand.errors.duplicate_term');

    return null;
}

/**
 * The single expression the whole report is read through.
 *
 * Literal terms are escaped and joined as alternatives under one case-insensitive flag, which is
 * the pattern the definition table prints verbatim. An empty definition compiles to null rather
 * than to an empty group: an expression matching everything would label the entire property as
 * brand traffic and look like an answer.
 */
export function compilePattern({ policy, terms, pattern }) {
    if (policy === MATCH_POLICY.REGEX) {
        const value = (pattern ?? '').trim();
        return value || null;
    }

    const alternatives = (terms ?? [])
        .map((term) => term.match.trim())
        .filter(Boolean)
        .map(escapeRegex);

    return alternatives.length ? `(?i)(${alternatives.join('|')})` : null;
}

/** Why a definition cannot be applied, as a list of reasons the scope bar shows. */
export function definitionErrors({ policy, terms, pattern }, t) {
    if (policy === MATCH_POLICY.REGEX) {
        const error = patternError(pattern, t);
        return error ? [error] : [];
    }

    if (!(terms ?? []).some((term) => term.match.trim())) {
        return [t('tools.brand_non_brand.errors.no_terms')];
    }

    const compiled = compilePattern({ policy, terms });
    if (compiled && compiled.length > MAX_PATTERN_LENGTH) {
        return [t('tools.brand_non_brand.errors.pattern_long', { max: MAX_PATTERN_LENGTH })];
    }

    return [];
}

/** The filter one side of the rule becomes, ready to spread into a request. */
export function segmentFilter(segment, pattern) {
    return { dimension: 'query', operator: SEGMENT_OPERATOR[segment], expression: pattern };
}

/**
 * Which of the applied literal terms appear in one returned query, matched the way the compiled
 * pattern matches them: as case-insensitive substrings.
 *
 * Only meaningful under the literal policy, where this app built the pattern and can therefore
 * say what it did. Under an advanced pattern the question is RE2's, not ours, and is left to it.
 */
export function matchingTerms(query, terms) {
    const text = (query ?? '').toLowerCase();
    return (terms ?? [])
        .map((term) => term.match.trim())
        .filter((match) => match && text.includes(match.toLowerCase()));
}

/**
 * Why one returned query is on the side it is on.
 *
 * Under a literal definition the reason is checkable, so it is checked: the terms found in the
 * query are named. A query that disagrees with the side Search Console returned it on — text that
 * looks like a match sitting among the excluded rows, or the reverse — is marked rather than
 * smoothed over, because that is the user's cue that a term is matching more or less than they
 * meant it to. It is not called an error: RE2 and this check are different implementations, and
 * Google's is the one that produced the figures.
 *
 * Under an advanced pattern nothing is re-derived. The row is evidence that the expression
 * returned it, and the reason says exactly that and no more — in particular it never claims to
 * know what the searcher intended.
 */
export function classificationReason(t, { policy, terms }, query, segment) {
    if (policy !== MATCH_POLICY.LITERAL) {
        return {
            text:
                segment === SEGMENT.BRAND
                    ? t('tools.brand_non_brand.evidence.reasons.pattern_included')
                    : t('tools.brand_non_brand.evidence.reasons.pattern_excluded'),
            ambiguous: false,
        };
    }

    const found = matchingTerms(query, terms);

    if (segment === SEGMENT.BRAND) {
        return found.length
            ? {
                  text: t('tools.brand_non_brand.evidence.reasons.contains', {
                      terms: found.join(', '),
                  }),
                  ambiguous: false,
              }
            : {
                  text: t('tools.brand_non_brand.evidence.reasons.included_no_term'),
                  ambiguous: true,
              };
    }

    return found.length
        ? {
              text: t('tools.brand_non_brand.evidence.reasons.excluded_contains', {
                  terms: found.join(', '),
              }),
              ambiguous: true,
          }
        : { text: t('tools.brand_non_brand.evidence.reasons.contains_none'), ambiguous: false };
}

/**
 * The brand side's share of the clicks that could be classified at all.
 *
 * The denominator is brand + non-brand, never the property's own total, and the page says so
 * everywhere the number appears. Search Console withholds queries below its own thresholds, so a
 * property total includes clicks from queries that were never offered to the rule; dividing by it
 * would quietly book every one of them as non-brand.
 *
 * Null rather than zero when there is nothing to divide, and null when either side failed to
 * come back — a share computed from one side and a blank is not a share.
 */
export function knownQueryShare(brandClicks, nonBrandClicks) {
    if (brandClicks === null || brandClicks === undefined) return null;
    if (nonBrandClicks === null || nonBrandClicks === undefined) return null;

    const total = brandClicks + nonBrandClicks;
    return total > 0 ? brandClicks / total : null;
}
